import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {debounceTime} from 'rxjs/operators';

import {SortDirection} from '../../../shared/ui/table/table.constants';
import {SortExpression, SortKey} from '../../../shared/ui/table/table.interfaces';
import {ContractRetroItem, RetroContractChangesData} from '../ongoing-payrolls.interfaces';

import {EmpItem, Filter} from './retro-changes.interfaces';

@Component({
  selector: 'gp-retro-changes',
  templateUrl: './retro-changes.component.html',
  styleUrls: ['./retro-changes.component.scss']
})
export class RetroChangesComponent implements OnInit {
  @Input()
  public contractChangesData: RetroContractChangesData;

  public retroList: ContractRetroItem[] = [];
  public filteredRetroList: ContractRetroItem[] = [];
  public retroDate: Date;

  public filtersGroup: UntypedFormGroup;
  public zoneTypes = [
    {name: 'PAYROLL_ONGOING.retro_emp_data', key: [EmpItem.EMP_DATA, EmpItem.EMPLOYEE]},
    {name: 'PAYROLL_ONGOING.retro_people_tree_node', key: [EmpItem.PEOPLE_TREE_NODE]},
    {name: 'PAYROLL_ONGOING.retro_emp_absence', key: [EmpItem.EMP_ABSENCE]},
    {name: 'PAYROLL_ONGOING.retro_emp_variable', key: [EmpItem.EMP_VARIABLE]},
    {name: 'PAYROLL_ONGOING.retro_emp_contract', key: [EmpItem.EMP_CONTRACT]},
    {name: 'PAYROLL_ONGOING.retro_rep_entity', key: [EmpItem.REP_ENTITY, EmpItem.REP_DATA]}
  ];
  public filters: Filter[] = [
    {
      key: 'zoneType',
      operation: (retro: ContractRetroItem, val: string): boolean => {
        return val.includes(retro.item);
      }
    },
    {
      key: 'groupZoneTransId',
      operation: (retro: ContractRetroItem, val: string): boolean => {
        return this.translate.instant(retro.groupZoneTransId).toLowerCase().indexOf(val.toLowerCase()) > -1;
      }
    }
  ];
  public sortKey: SortKey = [
    {key: 'retroDate', direction: SortDirection.ASCENDING}
  ];
  public directions: any = SortDirection;
  public filtersVisible: boolean;

  constructor(private formBuilder: UntypedFormBuilder,
              private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.retroList = [].concat(
      this.contractChangesData.groupZoneRetroList,
      this.contractChangesData.variableRetroList,
      this.contractChangesData.absenceRetroList);

    this.initFilters();
    this.update();
    this.retroDate = this.contractChangesData.retro || this.filteredRetroList[0].retroDate;
  }

  private initFilters() {
    this.filtersGroup = this.formBuilder.group({
      zoneType: [''],
      groupZoneTransId: ['']
    });
    this.filtersGroup.valueChanges
      .pipe(
        debounceTime(100)
      )
      .subscribe(() => {
        this.update();
      });
  }

  public update() {
    const allFilters = this.filtersGroup.value;

    const activeFilters = Object.keys(allFilters)
      .filter(filterKey => !!allFilters[filterKey])
      .map(filterKey => this.filters.find(filter => filter.key === filterKey));

    this.filteredRetroList = this.retroList
      .filter(retro => activeFilters.length === 0 || activeFilters.every(filter => filter.operation(retro, allFilters[filter.key])))
      .sort((a, b) => {
        const sortKey = this.sortKey[0];
        const a1 = sortKey.direction === SortDirection.ASCENDING ? a : b;
        const b1 = sortKey.direction === SortDirection.ASCENDING ? b : a;
        return +new Date(a1[sortKey.key]) - +new Date(b1[sortKey.key]);
      });
  }

  public getRetroItemKey(retro: ContractRetroItem): string {
    let item: string;
    switch (retro.item) {
      case EmpItem.EMPLOYEE:
        item = EmpItem.EMP_DATA;
        break;
      case EmpItem.REP_DATA:
        item = EmpItem.REP_ENTITY;
        break;
      default:
        item = retro.item;
        break;
    }
    return 'PAYROLL_ONGOING.retro_' + item.toLowerCase();
  }

  public isFilterSet(name: string, value: string): boolean {
    return this.filtersGroup.value[name] === value;
  }

  public toggleColumnSort(key: string) {
    const existing: SortExpression = this.sortKey.find(k => k.key === key);
    if (existing) {
      existing.direction = existing.direction === SortDirection.ASCENDING ?
        SortDirection.DESCENDING : SortDirection.ASCENDING;
    } else {
      this.sortKey = [{key, direction: SortDirection.ASCENDING}];
    }
    this.update();
  }

  public getColumnSortDirection(key: string) {
    const existing: SortExpression = this.sortKey.find(k => k.key === key);
    return existing ? existing.direction : null;
  }
}
